import { useEffect, useState } from "react";
import { X, Mail, Loader2, CheckCircle2, Unlink, Link2, Copy, ExternalLink, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const Copyable = ({ label, value, testid }) => (
  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className="text-xs font-mono text-slate-700 truncate" data-testid={testid}>{value}</div>
    </div>
    <button type="button" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copiato"); }} className="p-1.5 rounded hover:bg-white text-slate-500" title="Copia"><Copy className="w-3.5 h-3.5" /></button>
  </div>
);

const SetupForm = ({ cfg, onSaved }) => {
  const [cid, setCid] = useState(cfg.client_id || "");
  const [sec, setSec] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.gmailConfigSave({ client_id: cid, client_secret: sec }); toast.success("Credenziali Google salvate"); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-3" data-testid="gmail-setup-form">
      <div className="text-sm text-slate-600 space-y-1.5">
        <p className="font-semibold text-slate-800 flex items-center gap-1.5"><Settings2 className="w-4 h-4" /> Configurazione iniziale (una sola volta)</p>
        <p>Google richiede che l'app sia registrata sul tuo account Google Cloud. Poi il pulsante <strong>Collega Gmail</strong> funzionerà con un click e l'autorizzazione sarà automatica.</p>
        <ol className="list-decimal pl-5 space-y-1 text-xs text-slate-600">
          <li><a href="https://console.cloud.google.com/apis/library/gmail.googleapis.com" target="_blank" rel="noreferrer" className="text-orange-600 underline inline-flex items-center gap-1">Abilita Gmail API <ExternalLink className="w-3 h-3" /></a> (crea un progetto se richiesto)</li>
          <li><a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noreferrer" className="text-orange-600 underline inline-flex items-center gap-1">Schermata consenso OAuth <ExternalLink className="w-3 h-3" /></a>: tipo Esterno, aggiungi la tua Gmail tra gli <em>Utenti di test</em></li>
          <li><a href="https://console.cloud.google.com/apis/credentials/oauthclient" target="_blank" rel="noreferrer" className="text-orange-600 underline inline-flex items-center gap-1">Crea ID client OAuth <ExternalLink className="w-3 h-3" /></a> → Applicazione web, incollando i due valori qui sotto</li>
        </ol>
      </div>
      <Copyable label="Origine JavaScript autorizzata" value={cfg.origin} testid="gmail-origin" />
      <Copyable label="URI di reindirizzamento autorizzato" value={cfg.redirect_uri} testid="gmail-redirect-uri" />
      <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Client ID (…apps.googleusercontent.com)" data-testid="gmail-client-id-input" required
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
      <input value={sec} onChange={(e) => setSec(e.target.value)} placeholder="Client Secret" type="password" data-testid="gmail-client-secret-input" required
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
      <button type="submit" disabled={busy} data-testid="gmail-config-save" className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Salva e prosegui
      </button>
    </form>
  );
};

export const GmailConnectModal = ({ onClose }) => {
  const [status, setStatus] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [showSetup, setShowSetup] = useState(false);

  const load = () => Promise.all([api.gmailStatus(), api.gmailConfig()]).then(([s, c]) => { setStatus(s); setCfg(c); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const disconnect = async () => { await api.gmailDisconnect(); toast.success("Gmail scollegato"); load(); };
  const resetConfig = async () => {
    if (!window.confirm("Rimuovere le credenziali Google e scollegare Gmail?")) return;
    await api.gmailConfigDelete(); toast.success("Configurazione rimossa"); setShowSetup(false); load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="gmail-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"><Mail className="w-5 h-5" /></div>
            <div>
              <h3 className="font-display font-bold text-slate-900">Google email</h3>
              <p className="text-xs text-slate-500">Collegamento Gmail con consenso separato dall'accesso a ELLEBIERRE</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="gmail-modal-close"><X className="w-4 h-4" /></button>
        </div>

        {!status || !cfg ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" /></div> : status.connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="gmail-connected-box">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-emerald-800">Gmail collegato</div>
                <div className="text-xs text-emerald-700 font-mono truncate">{status.email}</div>
                <div className="text-[11px] text-emerald-700/80 mt-0.5">{status.total_emails} email sincronizzate · controllo automatico ogni {status.auto_sync_minutes} min{status.last_sync ? ` · ultimo: ${new Date(status.last_sync).toLocaleString("it-IT")}` : ""}</div>
              </div>
            </div>
            <button onClick={disconnect} data-testid="gmail-disconnect-button" className="w-full inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold py-2.5 rounded-lg"><Unlink className="w-4 h-4" /> Scollega Gmail</button>
          </div>
        ) : status.configured && !showSetup ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Se hai già effettuato l'accesso a ELLEBIERRE con Google, non servono nuove credenziali: premi <strong>Collega Gmail</strong> e autorizza Google ad accedere alle email. La lettura e l'invio richiedono un consenso aggiuntivo rispetto al semplice login.</p>
            {cfg.source === "login" && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3">Il client OAuth è già condiviso con l'accesso Google. In Google Cloud deve essere autorizzato anche questo URI di reindirizzamento: <code className="break-all">{cfg.redirect_uri}</code>.</p>}
            <a href={api.gmailLoginUrl()} data-testid="gmail-connect-button" className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold py-3 rounded-lg">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.6C16.9 3 14.7 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z"/></svg>
              Collega Gmail con Google
            </a>
            <div className="flex justify-between text-xs text-slate-400">
              <span className="font-mono truncate max-w-[70%]" title={cfg.client_id}>ID: {cfg.client_id.slice(0, 18)}…</span>
              {cfg.source !== "login" && <button onClick={() => setShowSetup(true)} className="underline" data-testid="gmail-edit-config">modifica credenziali</button>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <SetupForm cfg={cfg} onSaved={() => { setShowSetup(false); load(); }} />
            {cfg.from_db && <button onClick={resetConfig} className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-red-500 hover:underline" data-testid="gmail-config-reset"><Trash2 className="w-3.5 h-3.5" /> Rimuovi credenziali salvate</button>}
          </div>
        )}
      </div>
    </div>
  );
};

export const openGmailModal = () => window.dispatchEvent(new Event("edil:gmail-modal"));
